<template>
    <div class="px-2 py-4 xl:px-10">
        <div class="max-w-8xl mx-auto w-full space-y-4">
            <!-- Page Header -->
            <div class="flex items-center justify-between">
                <h1 class="text-xl font-semibold text-gray-900 dark:text-white">
                    Question Bank Management
                </h1>
                <div class="flex items-center space-x-2">
                    <button
                        @click="openImportModal"
                        class="px-4 py-2 text-sm font-medium bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                    >
                        Import
                    </button>
                    <router-link
                        :to="{ name: 'QuestionBankCreate' }"
                        class="inline-flex items-center px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                    >
                        <Plus class="w-4 h-4 mr-1" />
                        Add Question
                    </router-link>
                </div>
            </div>

            <!-- Search and Filters -->
            <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6">
                <div class="grid grid-cols-1 md:grid-cols-6 gap-4">
                    <!-- Search -->
                    <div>
                        <label
                            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                        >
                            Search
                        </label>
                        <input
                            v-model="searchQuery"
                            @input="debouncedSearch"
                            type="text"
                            placeholder="Search by question text, explanation, or tags..."
                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                        />
                    </div>

                    <!-- Subject Filter -->
                    <div>
                        <SearchableSelect
                            v-model="filters.subject_id"
                            :options="subjectOptions"
                            label="Subject"
                            placeholder="All Subjects"
                            option-label="name"
                            option-value="id"
                            @update:model-value="fetchQuestions"
                        />
                    </div>

                    <!-- Teacher Filter - Only visible for administrators and super-administrators -->
                    <div v-if="canViewTeacherFilter">
                        <SearchableSelect
                            v-model="filters.teacher_id"
                            :options="teacherOptions"
                            label="Teacher"
                            placeholder="All Teachers"
                            :option-label="getTeacherName"
                            option-value="id"
                            @update:model-value="fetchQuestions"
                        />
                    </div>

                    <!-- Type Filter -->
                    <div>
                        <SearchableSelect
                            v-model="filters.type"
                            :options="typeOptions"
                            label="Type"
                            placeholder="All Types"
                            option-label="label"
                            option-value="value"
                            @update:model-value="fetchQuestions"
                        />
                    </div>

                    <!-- Difficulty Filter -->
                    <div>
                        <SearchableSelect
                            v-model="filters.difficulty"
                            :options="difficultyOptions"
                            label="Difficulty"
                            placeholder="All Levels"
                            option-label="label"
                            option-value="value"
                            @update:model-value="fetchQuestions"
                        />
                    </div>

                    <!-- Tags Filter -->
                    <div>
                        <label
                            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                        >
                            Tags
                        </label>
                        <input
                            v-model="filters.tags"
                            @input="debouncedSearch"
                            type="text"
                            placeholder="Search tags..."
                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                        />
                    </div>

                    <!-- Status Filter -->
                    <StatusFilter
                        v-model="filters.is_active"
                        @change="fetchQuestions"
                    />

                    <!-- Page Length Selector -->
                    <PageLengthSelector
                        v-model="questionBankStore.perPage"
                        @update:model-value="handlePerPageChange"
                    />
                </div>
                <div class="flex justify-between items-center mt-4">
                    <button
                        @click="resetFilters()"
                        v-if="hasActiveFilters"
                        class="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                    >
                        <RotateCcw class="w-4 h-4 mr-1" />
                        Reset Filters
                    </button>
                </div>
            </div>

            <!-- Loading State -->
            <div
                v-if="questionBankStore.loading"
                class="flex justify-center items-center py-12"
            >
                <div
                    class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"
                ></div>
            </div>

            <!-- Error State -->
            <div
                v-if="questionBankStore.error"
                class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6"
            >
                <div class="flex">
                    <div class="flex-shrink-0">
                        <div class="h-5 w-5 text-red-400">⚠️</div>
                    </div>
                    <div class="ml-3">
                        <h3
                            class="text-sm font-medium text-red-800 dark:text-red-200"
                        >
                            Error
                        </h3>
                        <p class="mt-1 text-sm text-red-700 dark:text-red-300">
                            {{ questionBankStore.error }}
                        </p>
                        <button
                            @click="questionBankStore.clearError"
                            class="mt-2 text-sm text-red-600 dark:text-red-400 hover:text-red-500"
                        >
                            Dismiss
                        </button>
                    </div>
                </div>
            </div>

            <!-- Data Table -->
            <div
                v-if="!questionBankStore.loading && !questionBankStore.error"
                class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden"
            >
                <div
                    class="overflow-x-auto bg-gray-800 dark:bg-gray-900 shadow-md rounded-lg"
                >
                    <table
                        class="min-w-full divide-y divide-gray-200 dark:divide-gray-700"
                        style="width: 100%"
                    >
                        <thead class="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Question
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Type
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Subject
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Teacher
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Difficulty
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Points
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Status
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody
                            class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700"
                        >
                            <tr
                                v-for="question in questionBankStore.questions"
                                :key="question.id"
                                class="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div
                                        class="text-sm font-medium text-gray-900 dark:text-white"
                                    >
                                        {{ question.question?.substring(0, 100)
                                        }}{{
                                            question.question?.length > 100
                                                ? "..."
                                                : ""
                                        }}
                                    </div>
                                    <div
                                        v-if="question.question?.length > 100"
                                        class="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs"
                                        :title="question.question"
                                    >
                                        {{ question.question }}
                                    </div>
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                                >
                                    <span
                                        class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                                        :class="getTypeClass(question.type)"
                                    >
                                        {{ question.type }}
                                    </span>
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                                >
                                    {{ question.subject?.name || "-" }}
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                                >
                                    {{ question.teacher?.user?.name || "-" }}
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                                >
                                    <span
                                        class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                                        :class="
                                            getDifficultyClass(
                                                question.difficulty,
                                            )
                                        "
                                    >
                                        {{ question.difficulty }}
                                    </span>
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                                >
                                    {{ question.points }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <span
                                        v-if="question.is_active"
                                        class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                                    >
                                        Active
                                    </span>
                                    <span
                                        v-else
                                        class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                                    >
                                        Inactive
                                    </span>
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm font-medium"
                                >
                                    <button
                                        @click="openDetailModal(question.id)"
                                        title="View Question"
                                        class="inline-flex items-center justify-center p-2 text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300 rounded-md hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors mr-2"
                                    >
                                        <Eye class="w-4 h-4" />
                                    </button>
                                    <router-link
                                        :to="{
                                            name: 'QuestionBankEdit',
                                            params: { id: question.id },
                                        }"
                                        title="Edit Question"
                                        class="inline-flex items-center justify-center p-2 text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors mr-2"
                                    >
                                        <Pencil class="w-4 h-4" />
                                    </router-link>
                                    <button
                                        @click="confirmDelete(question)"
                                        title="Delete Question"
                                        class="inline-flex items-center justify-center p-2 text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                    >
                                        <Trash2 class="w-4 h-4" />
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Empty State -->
                <div
                    v-if="questionBankStore.questions.length === 0"
                    class="text-center py-12"
                >
                    <BookOpen class="mx-auto h-12 w-12 text-gray-400" />
                    <h3
                        class="mt-2 text-sm font-medium text-gray-900 dark:text-white"
                    >
                        No questions found
                    </h3>
                    <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Get started by adding a new question.
                    </p>
                    <div class="mt-6">
                        <router-link
                            :to="{ name: 'QuestionBankCreate' }"
                            class="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                        >
                            <Plus class="w-4 h-4 mr-1" />
                            Add Question
                        </router-link>
                    </div>
                </div>
            </div>

            <!-- Pagination -->
            <div
                v-if="
                    (questionBankStore.meta?.total || 0) >
                    (questionBankStore.meta?.per_page || 10)
                "
                class="bg-white dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6"
            >
                <div class="flex-1 flex justify-between sm:hidden">
                    <button
                        @click="previousPage"
                        :disabled="questionBankStore.meta?.current_page === 1"
                        class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                    >
                        Previous
                    </button>
                    <button
                        @click="nextPage"
                        :disabled="
                            questionBankStore.meta?.current_page ===
                            questionBankStore.meta?.last_page
                        "
                        class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                    >
                        Next
                    </button>
                </div>
                <div
                    class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between"
                >
                    <div>
                        <p class="text-sm text-gray-700 dark:text-gray-300">
                            Showing
                            <span class="font-medium">{{
                                questionBankStore.meta?.from ?? 0
                            }}</span>
                            to
                            <span class="font-medium">{{
                                questionBankStore.meta?.to ?? 0
                            }}</span>
                            of
                            <span class="font-medium">{{
                                questionBankStore.meta?.total ?? 0
                            }}</span>
                            results
                        </p>
                    </div>
                    <div>
                        <nav
                            class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                            aria-label="Pagination"
                        >
                            <button
                                @click="previousPage"
                                :disabled="
                                    questionBankStore.meta?.current_page === 1
                                "
                                class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                            >
                                Previous
                            </button>
                            <button
                                v-for="page in visiblePages"
                                :key="page"
                                @click="
                                    typeof page === 'number'
                                        ? goToPage(page)
                                        : null
                                "
                                :class="[
                                    'relative inline-flex items-center px-4 py-2 border text-sm font-medium',
                                    page ===
                                    questionBankStore.meta?.current_page
                                        ? 'z-10 bg-blue-50 border-blue-500 text-blue-600 dark:bg-blue-900 dark:text-blue-300'
                                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300',
                                    typeof page === 'string'
                                        ? 'cursor-not-allowed'
                                        : 'cursor-pointer',
                                ]"
                                :disabled="typeof page === 'string'"
                            >
                                {{ page }}
                            </button>
                            <button
                                @click="nextPage"
                                :disabled="
                                    questionBankStore.meta?.current_page ===
                                    questionBankStore.meta?.last_page
                                "
                                class="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                            >
                                Next
                            </button>
                        </nav>
                    </div>
                </div>
            </div>

            <!-- Question Bank Import Modal -->
            <QuestionBankImportModal
                :showModal="showImportModal"
                @close="closeImportModal"
                @imported="handleImported"
            />

            <!-- Delete Confirmation Modal -->
            <DeleteConfirmationModal
                v-if="showDeleteModal"
                :show="showDeleteModal"
                :title="deleteModalTitle"
                :message="deleteModalMessage"
                @confirm="handleDelete"
                @close="closeDeleteModal"
            />

            <!-- Question Detail Modal -->
            <QuestionBankDetailModal
                :show="showDetailModal"
                :questionId="selectedQuestionId"
                @close="closeDetailModal"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from "vue";
import { useQuestionBankStore } from "@/stores/questionBankStore";
import { useAuthStore } from "@/stores/authStore";
import SearchableSelect from "@/components/SearchableSelect.vue";
import StatusFilter from "@/components/StatusFilter.vue";
import PageLengthSelector from "@/components/PageLengthSelector.vue";
import QuestionBankImportModal from "../components/QuestionBankImportModal.vue";
import QuestionBankDetailModal from "../components/QuestionBankDetailModal.vue";
import DeleteConfirmationModal from "@/components/DeleteConfirmationModal.vue";
import NotificationService from "@/utils/notifications";
import subjectApi from "@/api/subject";
import teacherApi from "@/api/teacher";
import type { QuestionBank } from "@/types/questionBank";
import type { Subject } from "@/api/subject";
import type { Teacher } from "@/api/teacher";
import { Plus, RotateCcw, Pencil, Trash2, BookOpen, Eye } from "@lucide/vue";

const questionBankStore = useQuestionBankStore();
const authStore = useAuthStore();

// State
const searchQuery = ref("");
const filters = ref({
    subject_id: "", // Required field
    teacher_id: "", // Required field
    type: "", // Optional field
    difficulty: "", // Optional field
    tags: "", // Optional field
    search: "", // Optional field
    is_active: undefined as boolean | undefined, // Optional field
});
const showImportModal = ref(false);
const showDeleteModal = ref(false);
const showDetailModal = ref(false);
const selectedQuestion = ref<QuestionBank | null>(null);
const selectedQuestionId = ref<string | null>(null);

// Types for options with "Select All" option
type SubjectOption = Subject | { id: string; name: string };
type TeacherOption = Teacher | { id: ""; user: { name: "All Teachers" } };

const subjectOptions = ref<SubjectOption[]>([]);
const teacherOptions = ref<TeacherOption[]>([]);

// Delete modal properties
const deleteModalTitle = ref("Confirm Delete");
const deleteModalMessage = ref(
    "Are you sure you want to delete this question? This action cannot be undone.",
);

const visiblePages = computed(() => {
    const current = questionBankStore.meta?.current_page || 1;
    const last = questionBankStore.meta?.last_page || 1;
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (
        let i = Math.max(2, current - delta);
        i <= Math.min(last - 1, current + delta);
        i++
    ) {
        range.push(i);
    }

    if (current - delta > 2) {
        rangeWithDots.push(1, "...");
    } else {
        rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (current + delta < last - 1) {
        rangeWithDots.push("...", last);
    } else if (last > 1) {
        rangeWithDots.push(last);
    }

    return rangeWithDots;
});

const hasActiveFilters = computed(() => {
    return (
        searchQuery.value.trim() !== "" ||
        filters.value.subject_id !== "" ||
        filters.value.teacher_id !== "" ||
        filters.value.type !== "" ||
        filters.value.difficulty !== "" ||
        filters.value.tags !== "" ||
        filters.value.is_active !== undefined ||
        questionBankStore.page !== 1 ||
        questionBankStore.perPage !== 10
    );
});

// Options for Type filter
const typeOptions = computed(() => [
    { value: "", label: "All Types" },
    { value: "multiple_choice", label: "Multiple Choice" },
    { value: "multiple_select", label: "Multiple Select" },
    { value: "true_false", label: "True/False" },
    { value: "essay", label: "Essay" },
]);

// Options for Difficulty filter
const difficultyOptions = computed(() => [
    { value: "", label: "All Levels" },
    { value: "easy", label: "Easy" },
    { value: "medium", label: "Medium" },
    { value: "hard", label: "Hard" },
]);

// Role-based visibility for Teacher filter
// Teacher filter should be visible for administrators and super-administrators only
const canViewTeacherFilter = computed(() => {
    return authStore.hasAnyRole(["administrator", "super-administrator"]);
});

// Methods
const handlePerPageChange = (value: number) => {
    questionBankStore.perPage = value;
    questionBankStore.page = 1;
    fetchQuestions();
};

const fetchQuestions = async () => {
    const searchFilters = {
        ...filters.value,
        search: searchQuery.value,
    };
    await questionBankStore.fetchQuestions(searchFilters);
};

const resetFilters = () => {
    searchQuery.value = "";
    filters.value = {
        subject_id: "",
        teacher_id: "",
        type: "",
        difficulty: "",
        tags: "",
        search: "",
        is_active: undefined,
    };
    questionBankStore.setPage(1);
    questionBankStore.setPerPage(10);
    fetchQuestions();
};

const updateSubjectOptionsFromQuestions = () => {
    const seen = new Map<string, { id: string; name: string }>();
    questionBankStore.questions.forEach((q: QuestionBank) => {
        if (q.subject?.id && q.subject?.name && !seen.has(q.subject.id)) {
            seen.set(q.subject.id, { id: q.subject.id, name: q.subject.name });
        }
    });
    subjectOptions.value = [
        { id: "", name: "All Subjects" },
        ...Array.from(seen.values()),
    ];
};

const fetchOptions = async () => {
    if (!canViewTeacherFilter.value) {
        updateSubjectOptionsFromQuestions();
        teacherOptions.value = [{ id: "", user: { name: "All Teachers" } }];
        return;
    }

    try {
        const [subjectsResponse, teachersResponse] = await Promise.all([
            subjectApi.getAll({ per_page: 1000 }),
            teacherApi.getAll({ per_page: 1000 }),
        ]);
        subjectOptions.value = [
            { id: "", name: "All Subjects" },
            ...(subjectsResponse.data?.items || []),
        ];
        teacherOptions.value = [
            { id: "", user: { name: "All Teachers" } },
            ...(teachersResponse.data?.items || []),
        ];
    } catch {
        subjectOptions.value = [{ id: "", name: "All Subjects" }];
        teacherOptions.value = [{ id: "", user: { name: "All Teachers" } }];
    }
};

// For non-admin users, keep subject options in sync with loaded questions
watch(
    () => questionBankStore.questions,
    () => {
        if (!canViewTeacherFilter.value) {
            updateSubjectOptionsFromQuestions();
        }
    },
    { deep: true },
);

const debouncedSearch = useDebounce(fetchQuestions, 300);

const getTypeClass = (type: string) => {
    const typeClasses: Record<string, string> = {
        multiple_choice:
            "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
        multiple_select:
            "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200",
        true_false:
            "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
        essay: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
    };
    return (
        typeClasses[type] ||
        "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200"
    );
};

const getDifficultyClass = (difficulty: string) => {
    const difficultyClasses: Record<string, string> = {
        easy: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
        medium: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
        hard: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
    };
    return (
        difficultyClasses[difficulty] ||
        "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200"
    );
};

const getTeacherName = (teacher: TeacherOption): string => {
    return teacher.user?.name || "";
};

const openImportModal = () => {
    showImportModal.value = true;
};

const closeImportModal = () => {
    showImportModal.value = false;
};

const handleImported = () => {
    showNotification("Questions imported successfully", "success");
    fetchQuestions();
};

const confirmDelete = (question: QuestionBank) => {
    deleteModalTitle.value = "Delete Question";
    deleteModalMessage.value = `Are you sure you want to delete "${question.question?.substring(0, 100)}${question.question?.length > 100 ? "..." : ""}"? This action cannot be undone.`;
    selectedQuestion.value = question; // Set the question to be deleted
    showDeleteModal.value = true;
};

const closeDeleteModal = () => {
    showDeleteModal.value = false;
};

const handleDelete = async () => {
    try {
        if (selectedQuestion.value) {
            await questionBankStore.deleteQuestion(selectedQuestion.value.id);
            showNotification("Question deleted successfully", "success");
            closeDeleteModal();
        }
    } catch (error) {
        showNotification("Failed to delete question", "error");
    }
};

const openDetailModal = (questionId: string) => {
    selectedQuestionId.value = questionId;
    showDetailModal.value = true;
};

const closeDetailModal = () => {
    showDetailModal.value = false;
    selectedQuestionId.value = null;
};

const previousPage = () => {
    if ((questionBankStore.meta?.current_page || 1) > 1) {
        questionBankStore.setPage(
            (questionBankStore.meta?.current_page || 1) - 1,
        );
    }
};

const nextPage = () => {
    if (
        (questionBankStore.meta?.current_page || 1) <
        (questionBankStore.meta?.last_page || 1)
    ) {
        questionBankStore.setPage(
            (questionBankStore.meta?.current_page || 1) + 1,
        );
    }
};

const goToPage = (page: number) => {
    questionBankStore.setPage(page);
};

const showNotification = (
    message: string,
    type: "success" | "error" | "info" | "warning",
) => {
    switch (type) {
        case "success":
            NotificationService.success(message);
            break;
        case "error":
            NotificationService.error(message);
            break;
        case "info":
            NotificationService.info(message);
            break;
        case "warning":
            NotificationService.warning(message);
            break;
    }
};

// Debounce utility
function useDebounce(fn: Function, delay: number) {
    let timeoutId: ReturnType<typeof setTimeout>;
    return (...args: any[]) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn(...args), delay);
    };
}

// Lifecycle
onMounted(() => {
    fetchQuestions();
    fetchOptions();
});
</script>
